import type { ReactNode } from "react"
import { useTranslation } from "@/i18n"
import { Icon } from "@/components/Icon"
import type { PostFilterState } from "@/nostr/api/filters/filterEngine"

/** Props every panel section receives. */
export interface FilterCategoryProps {
  value: PostFilterState
  onChange: (patch: Partial<PostFilterState>) => void
  onReset: () => void
}

export interface FilterSectionProps {
  /** Panel section: content, time & engagement, or people & sources. */
  title: string
  id: string
  onReset?: () => void
  children: ReactNode
}

/**
 * One collapsible-free section of the filter panel: a heading, an optional
 * per-section Reset and its controls. Keeping the chrome here means every
 * category looks and behaves the same.
 */
export function FilterSection({ title, id, onReset, children }: FilterSectionProps) {
  const { t } = useTranslation()
  return (
    <section className="filter-section" aria-labelledby={id}>
      <div className="filter-section-head">
        <h3 id={id}>{title}</h3>
        {onReset && (
          <button type="button" className="btn btn-ghost btn-small" onClick={onReset}>
            <Icon name="refresh" size={13} />
            {t("Reset")}
          </button>
        )}
      </div>
      <div className="filter-section-body">{children}</div>
    </section>
  )
}

export interface FilterFieldProps {
  label: string
  children: ReactNode
  hint?: string
}

/** Label + control + optional hint, used by every filter control. */
export function FilterField({ label, children, hint }: FilterFieldProps) {
  return (
    <div className="filter-field">
      <span className="filter-field-label">{label}</span>
      {children}
      {hint && <p className="muted small filter-field-hint">{hint}</p>}
    </div>
  )
}

export interface FilterChipGroupProps<T extends string> {
  options: Array<{ value: T; label: string }>
  selected: readonly T[]
  onToggle: (value: T) => void
  ariaLabel: string
}

/** Toggle chips used by post type, media, language, time and source filters. */
export function FilterChipGroup<T extends string>({
  options,
  selected,
  onToggle,
  ariaLabel,
}: FilterChipGroupProps<T>) {
  return (
    <div className="filter-chip-group" role="group" aria-label={ariaLabel}>
      {options.map((option) => {
        const active = selected.includes(option.value)
        return (
          <button
            key={option.value}
            type="button"
            className={`chip filter-chip ${active ? "active" : ""}`.trim()}
            aria-pressed={active}
            onClick={() => onToggle(option.value)}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}
